<template>
  <component
    :is="as"
    class="text-shimmer-wave"
    :class="className"
    :style="rootStyle"
    role="status"
    aria-live="polite"
  >
    <span class="sr-only">{{ children }}</span>
    <span aria-hidden="true">
      <span
        v-for="(char, index) in characters"
        :key="`${index}-${char}`"
        class="text-shimmer-wave-char"
        :style="charStyle(index)"
      >{{ char }}</span>
    </span>
  </component>
</template>

<script>
export default {
  name: 'TextShimmerWave',
  props: {
    children: { type: String, required: true },
    as: { type: String, default: 'p' },
    className: { type: String, default: '' },
    duration: { type: Number, default: 1 },
    baseColor: { type: String, default: '' },
    shimmerColor: { type: String, default: '' },
    zDistance: { type: Number, default: 10 },
    xDistance: { type: Number, default: 2 },
    yDistance: { type: Number, default: -2 },
    spread: { type: Number, default: 1 },
    scaleDistance: { type: Number, default: 1.1 },
    rotateYDistance: { type: Number, default: 10 },
  },
  computed: {
    characters() {
      return Array.from(this.children);
    },
    rootStyle() {
      return {
        '--base-color': this.baseColor || 'color-mix(in oklab, currentColor 55%, transparent)',
        '--shimmer-color': this.shimmerColor || 'currentColor',
        '--wave-duration': `${Math.max(0.4, this.duration)}s`,
        '--wave-cycle': `${Math.max(0.7, this.duration + (this.characters.length * 0.05) / Math.max(0.25, this.spread))}s`,
        '--wave-z': `${this.zDistance}px`,
        '--wave-x': `${this.xDistance}px`,
        '--wave-y': `${this.yDistance}px`,
        '--wave-scale': this.scaleDistance,
        '--wave-rotate-y': `${this.rotateYDistance}deg`,
      };
    },
  },
  methods: {
    charStyle(index) {
      const delay = (index * this.duration) / (Math.max(0.25, this.spread) * Math.max(1, this.characters.length));
      return { animationDelay: `${delay}s` };
    },
  },
};
</script>

<style scoped>
.text-shimmer-wave {
  position: relative;
  display: inline-block;
  margin: 0;
  perspective: 500px;
}

.text-shimmer-wave-char {
  display: inline-block;
  white-space: pre;
  color: var(--base-color);
  transform-style: preserve-3d;
  animation: text-shimmer-wave var(--wave-cycle) ease-in-out infinite;
  will-change: transform, color;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@keyframes text-shimmer-wave {
  0%, 62%, 100% {
    color: var(--base-color);
    transform: translate3d(0, 0, 0) scale(1) rotateY(0);
  }
  31% {
    color: var(--shimmer-color);
    transform: translate3d(var(--wave-x), var(--wave-y), var(--wave-z)) scale(var(--wave-scale)) rotateY(var(--wave-rotate-y));
  }
}

@media (prefers-reduced-motion: reduce) {
  .text-shimmer-wave-char {
    color: var(--shimmer-color);
    animation: none;
    transform: none;
  }
}
</style>
